import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { fetchData } from "../utils/api.js";
import {
  CalendarIcon,
  FallIcon,
  SpringIcon,
  SummerIcon,
  WinterIcon,
} from "./common/Icons.js";
import { ChipMedallion } from "./common/ChipMedallion.js";

const ensureTeamStanding = (standingsMap, teamId) => {
  if (teamId && !standingsMap[teamId]) {
    standingsMap[teamId] = {
      id: teamId,
      name: "Unknown",
      wins: 0,
      losses: 0,
      ties: 0,
      rs: 0,
      ra: 0,
    };
  }
};

const applyGameResult = (standingsMap, game) => {
  const {
    home_team_id: hId,
    away_team_id: aId,
    home_score: hs,
    away_score: as,
  } = game;
  if (hs === null || hs === undefined || as === null || as === undefined) {
    return;
  }
  ensureTeamStanding(standingsMap, hId);
  ensureTeamStanding(standingsMap, aId);

  const home = standingsMap[hId];
  const away = standingsMap[aId];
  if (home) {
    home.rs += hs;
    home.ra += as;
  }
  if (away) {
    away.rs += as;
    away.ra += hs;
  }

  if (hs > as) {
    if (home) home.wins += 1;
    if (away) away.losses += 1;
  } else if (as > hs) {
    if (away) away.wins += 1;
    if (home) home.losses += 1;
  } else {
    if (home) home.ties += 1;
    if (away) away.ties += 1;
  }
};

const sortStandings = (standingsList) =>
  [...standingsList].sort((a, b) => {
    const totalA = a.wins + a.losses + a.ties || 1;
    const totalB = b.wins + b.losses + b.ties || 1;
    const wpA = (a.wins + a.ties * 0.5) / totalA;
    const wpB = (b.wins + b.ties * 0.5) / totalB;
    if (wpB !== wpA) return wpB - wpA;
    const diffA = a.rs - a.ra;
    const diffB = b.rs - b.ra;
    if (diffB !== diffA) return diffB - diffA;
    return a.name.localeCompare(b.name);
  });

const SeasonTypeIcon = ({ label }) => {
  const lower = (label || "").toLowerCase();
  if (lower.includes("fall")) return <FallIcon size="sm" />;
  if (lower.includes("spring")) return <SpringIcon size="sm" />;
  if (lower.includes("summer")) return <SummerIcon size="sm" />;
  if (lower.includes("winter")) return <WinterIcon size="sm" />;
  return <CalendarIcon size="sm" />;
};

SeasonTypeIcon.propTypes = {
  label: PropTypes.string,
};

const matchesSelectedMixin = (
  divisionName,
  selectedGender,
  selectedDivision,
) => {
  if (!divisionName) return false;
  const lower = divisionName.toLowerCase();
  const genderOk =
    !selectedGender || lower.includes(selectedGender.toLowerCase());
  const divOk =
    !selectedDivision || lower.includes(selectedDivision.toLowerCase());
  return genderOk && divOk;
};

const filterByDivisionMixin = (teams, games, divisions, gender, division) => {
  if (
    (!gender && !division) ||
    !Array.isArray(divisions) ||
    !divisions.length
  ) {
    return { filteredTeams: teams, filteredGames: games };
  }
  const matchingIds = new Set(
    divisions
      .filter((d) => matchesSelectedMixin(d.name, gender, division))
      .map((d) => String(d.division_id || d.id)),
  );
  if (matchingIds.size === 0) {
    return { filteredTeams: teams, filteredGames: games };
  }
  const taggedTeams = teams.filter(
    (t) => t.division_id && matchingIds.has(String(t.division_id)),
  );
  if (taggedTeams.length === 0) {
    return { filteredTeams: teams, filteredGames: games };
  }
  const taggedTeamIds = new Set(
    taggedTeams.map((t) => String(t.team_id || t.id)),
  );
  const taggedGames = games.filter(
    (g) =>
      (g.division_id && matchingIds.has(String(g.division_id))) ||
      taggedTeamIds.has(String(g.home_team_id)) ||
      taggedTeamIds.has(String(g.away_team_id)),
  );
  return { filteredTeams: taggedTeams, filteredGames: taggedGames };
};

const formatSeasonDisplayName = (season) => {
  if (!season) return "";
  if (season.name) return season.name;
  return [season.season_type || season.season, season.year]
    .filter(Boolean)
    .join(" ");
};

const resolveStandingsSeasons = async (league, selectedSeasonId) => {
  if (selectedSeasonId) {
    return { seasonIds: [selectedSeasonId], fallbackSeason: null };
  }
  const leagueId = league.league_id || league.id;
  const seasons = (await fetchData(`/seasons?league_id=eq.${leagueId}`)) || [];
  const seasonIds = seasons.map((s) => s.season_id || s.id).filter(Boolean);
  return { seasonIds, fallbackSeason: seasons[0] || null };
};

const computeStandingsList = (teams, games) => {
  const standingsMap = {};
  teams.forEach((t) => {
    const id = t.team_id || t.id;
    standingsMap[id] = {
      id,
      name: t.team_name || t.name || "Unknown Team",
      wins: 0,
      losses: 0,
      ties: 0,
      rs: 0,
      ra: 0,
    };
  });
  games.forEach((g) => {
    applyGameResult(standingsMap, g);
  });
  return sortStandings(Object.values(standingsMap));
};

const LeagueStandings = ({
  league,
  selectedSeason = null,
  selectedGender = "",
  selectedDivision = "",
  userTeamIds = [],
}) => {
  const [standings, setStandings] = useState([]);
  const [resolvedSeason, setResolvedSeason] = useState(selectedSeason);
  const [isLoading, setIsLoading] = useState(true);

  const selectedSeasonId = selectedSeason?.season_id || selectedSeason?.id;

  useEffect(() => {
    setResolvedSeason(selectedSeason);
  }, [selectedSeason]);

  useEffect(() => {
    const loadStandings = async () => {
      setIsLoading(true);
      try {
        const { seasonIds, fallbackSeason } = await resolveStandingsSeasons(
          league,
          selectedSeasonId,
        );
        if (fallbackSeason) {
          setResolvedSeason((prev) => prev || fallbackSeason);
        }
        if (seasonIds.length === 0) {
          setStandings([]);
          return;
        }

        const inClause = seasonIds.join(",");
        const hasMixinFilter = Boolean(selectedGender || selectedDivision);
        const [teamsRes, gamesRes, divsRes] = await Promise.all([
          fetchData(`/teams?season_id=in.(${inClause})`),
          fetchData(`/games?season_id=in.(${inClause})&status=eq.completed`),
          hasMixinFilter
            ? fetchData(`/divisions?season_id=in.(${inClause})`).catch(() => [])
            : Promise.resolve([]),
        ]);

        const { filteredTeams, filteredGames } = filterByDivisionMixin(
          teamsRes || [],
          gamesRes || [],
          divsRes || [],
          selectedGender,
          selectedDivision,
        );
        setStandings(computeStandingsList(filteredTeams, filteredGames));
      } catch (err) {
        console.error("Failed to load standings", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (league) {
      void loadStandings();
    }
  }, [league, selectedSeasonId, selectedGender, selectedDivision]);

  const seasonLabel = formatSeasonDisplayName(resolvedSeason);
  const mixinLabel = [selectedGender, selectedDivision]
    .filter(Boolean)
    .join(" - ");
  const registeredSet = new Set(userTeamIds.map(String));

  let tableContent;
  if (isLoading) {
    tableContent = <div className="loading-spinner" />;
  } else if (standings.length === 0) {
    tableContent = (
      <div style={{ padding: "1rem 0", color: "var(--color-text-secondary)" }}>
        No standings available yet.
      </div>
    );
  } else {
    tableContent = (
      <div style={{ overflowX: "auto" }}>
        <table
          className="admin-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            textAlign: "left",
          }}
        >
          <thead>
            <tr style={{ borderBottom: "2px solid var(--color-border)" }}>
              <th style={{ padding: "12px 16px" }}>Team</th>
              <th style={{ padding: "12px 16px" }}>W</th>
              <th style={{ padding: "12px 16px" }}>L</th>
              <th style={{ padding: "12px 16px" }}>T</th>
              <th style={{ padding: "12px 16px" }}>PCT</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((t) => {
              const total = t.wins + t.losses + t.ties;
              const pct =
                total === 0
                  ? ".000"
                  : ((t.wins + t.ties * 0.5) / total)
                      .toFixed(3)
                      .replace(/^0(?=\.)/, "");
              const isUserRegistered = registeredSet.has(String(t.id));
              return (
                <tr
                  key={t.id}
                  style={{ borderBottom: "1px solid var(--color-border)" }}
                >
                  <td style={{ padding: "12px 16px", fontWeight: 600 }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span>{t.name}</span>
                      {isUserRegistered && (
                        <span
                          data-testid="registered-team-indicator"
                          title="Your registered team"
                          style={{
                            width: "18px",
                            height: "18px",
                            borderRadius: "50%",
                            border: "1.5px solid var(--color-turquoise)",
                            color: "var(--color-turquoise)",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          R
                        </span>
                      )}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>{t.wins}</td>
                  <td style={{ padding: "12px 16px" }}>{t.losses}</td>
                  <td style={{ padding: "12px 16px" }}>{t.ties}</td>
                  <td style={{ padding: "12px 16px" }}>{pct}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="league-standings-section" style={{ marginBottom: "2rem" }}>
      {(seasonLabel || mixinLabel) && (
        <div
          className="standings-context-header"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            marginBottom: "1rem",
          }}
        >
          {seasonLabel && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "1.05rem",
                fontWeight: 600,
                color: "var(--color-text-primary)",
              }}
            >
              <span>
                {league.name} - {seasonLabel}
              </span>
              <SeasonTypeIcon label={seasonLabel} />
            </div>
          )}
          {mixinLabel && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "0.95rem",
                fontWeight: 500,
                color: "var(--color-text-secondary)",
              }}
            >
              <span>{mixinLabel}</span>
              {selectedGender && <ChipMedallion label={selectedGender} />}
              {selectedDivision && <ChipMedallion label={selectedDivision} />}
            </div>
          )}
        </div>
      )}
      {tableContent}
    </div>
  );
};

LeagueStandings.propTypes = {
  league: PropTypes.object.isRequired,
  selectedSeason: PropTypes.object,
  selectedGender: PropTypes.string,
  selectedDivision: PropTypes.string,
  userTeamIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
};

export default LeagueStandings;
